"use client";

import { rainbowCustomTheme } from "@/config/rainbow.config";
import { createConfigWithRainbowKit } from "@/utils/wagmi/create-config-with-rainbowkit.util";
import { RainbowKitProvider } from "@rainbow-me/rainbowkit";
import "@rainbow-me/rainbowkit/styles.css";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import React, { useMemo, useState } from "react";
import { WagmiProvider, type State } from "wagmi";

export type WagmiConfigType = "all_chains" | "dispenser";

type Props = {
  children: React.ReactNode;
  wagmiConfigType: WagmiConfigType;
  initialState?: State | undefined;
};

/**
 * CryptoProvider sets up the necessary context providers
 * for managing cryptocurrency-related functionality in the application.
 * It integrates Wagmi, RainbowKit, and React Query.
 *
 * @param {object} props - The properties passed to the CryptoProvider component.
 * @param {React.ReactNode} props.children - The child components to be rendered within the provider.
 * @param {any} props.wagmiConfigType - The configuration object for initializing the Wagmi provider.
 * @param {any} props.initialState - The initial state for the Wagmi provider.
 *
 * @returns {React.ReactElement<any>} A React element that wraps the children with the necessary providers.
 */

const queryClient = new QueryClient();

const CryptoProvider = ({
  children,
  wagmiConfigType,
  initialState,
}: Props): React.ReactElement<any> => {
  const config = useMemo(
    () => createConfigWithRainbowKit(wagmiConfigType),
    [wagmiConfigType],
  );

  return (
    <WagmiProvider config={config} initialState={initialState}>
      <QueryClientProvider client={queryClient}>
        <RainbowKitProvider
          theme={rainbowCustomTheme}
          modalSize="compact"
          showRecentTransactions
        >
          {children}
        </RainbowKitProvider>
      </QueryClientProvider>
    </WagmiProvider>
  );
};

export default CryptoProvider;
